import { useCallback } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import { getDestinationImage } from 'lib/search/searchUtils'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import ModalContent from 'LuxKit/Modal/ModalContent'
import HeroImage from 'content/components/HeroImage'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { usePlaceById } from 'hooks/usePlace'

interface Props {
  placeId: string
  onAddDestination: (e: React.MouseEvent<any>) => void
  destinationSaved: boolean
}

const StyledHeroImage = styled(HeroImage)`
&& {
  min-height: unset;
  height: ${rem(167)};
}
`

function AddDestinationToggleModal(props: Props) {
  const { placeId, destinationSaved, onAddDestination } = props
  const intl = useIntl()
  const place = usePlaceById(placeId)
  const placeName = place?.name
  const destinationImage = getDestinationImage(placeId)
  const modalElement = useModalElementContext()

  const handleSkip = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])

  const handleAddDestination = useCallback<React.MouseEventHandler<any>>((e) => {
    onAddDestination(e)
    handleSkip()
  }, [onAddDestination, handleSkip])

  const alreadyInListHeading = placeName ? intl.formatMessage({
    id: '4FhgT-dotcncipiwGRM5',
    defaultMessage: '{placeName} is already in your favourite destination list!',
    description: 'Heading shown when destination is already saved on the hotel search page',
  }, { placeName }) : undefined

  const addToPreferencesHeading = placeName ? intl.formatMessage({
    id: 'k0iA5JczEpJ7HjMreAMg',
    defaultMessage: 'Do you want to add {placeName} to your travel preferences?',
    description: 'Heading asking user to add destination to preferences',
  }, { placeName }) : undefined

  const alreadySavedMessage = intl.formatMessage({
    id: 'Nt8cXjKLux7cLLlfUuoj',
    defaultMessage: 'You’re all set to receive tailored recommendations and offers for it',
    description: 'Message shown when destination is already in favourites',
  })

  const getTailoredRecommendationsMessage = intl.formatMessage({
    id: '8UaIX-JSd93QyEipJK1T',
    defaultMessage: 'Get tailored recommendations to ensure you never miss an offer in your favourite destinations',
    description: 'Message encouraging user to add destination to preferences',
  })

  const okGotItText = intl.formatMessage({
    id: '6tVE3DW7bSHX4WOvcj0b',
    defaultMessage: 'Ok got it!',
    description: 'Button text to acknowledge destination is already saved',
  })

  const editPreferencesText = intl.formatMessage({
    id: 'wQt0d6d4p6ntgDmAETcH',
    defaultMessage: 'Edit preferences',
    description: 'Button text that navigates the user to the edit travel preferences page',
  })

  const addToWatchlistText = intl.formatMessage({
    id: 'PT0BnvDP5IJrxHDodkr1',
    defaultMessage: 'Add to Watchlist',
    description: 'Action button label to add destination to watchlist',
  })

  const skipForNowText = intl.formatMessage({
    id: 'LRuOiFwW5RxN6ssrHYeP',
    defaultMessage: 'Skip for now',
    description: 'Action button label to skip adding destination on the hotel search page',
  })

  const heading = destinationSaved ? alreadyInListHeading : addToPreferencesHeading

  return (
    <ModalBase>
      <ModalHeader title="" isOverlay />
      { destinationImage && <StyledHeroImage id={destinationImage} />}
      <ModalContent>
        <ModalBody>
          <VerticalSpacer gap={4}>
            {heading && (
              <HeadingTextBlock variant="heading4" align="center" tabletAlign="start">
                {heading}
              </HeadingTextBlock>
            )}
            <BodyText variant="large" align="center" tabletAlign="start">
              {destinationSaved ? alreadySavedMessage : getTailoredRecommendationsMessage}
            </BodyText>
          </VerticalSpacer>
        </ModalBody>
        <ModalFooter>
          <Group direction="vertical" tabletDirection="horizontal-reverse">
            {destinationSaved && (
              <>
                <TextButton kind="primary" onClick={handleSkip}>{okGotItText}</TextButton>
                <TextButton kind="tertiary" to="/account/travel-preferences#destinations">{editPreferencesText}</TextButton>
              </>
            )}
            {!destinationSaved && (
              <>
                <TextButton kind="primary" onClick={handleAddDestination}>{addToWatchlistText}</TextButton>
                <TextButton kind="tertiary" onClick={handleSkip}>{skipForNowText}</TextButton>
              </>
            )}
          </Group>
        </ModalFooter>
      </ModalContent>
    </ModalBase>
  )
}

export default AddDestinationToggleModal
